import dayjs, { Dayjs } from "dayjs";
import Badge from "@mui/material/Badge";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DayCalendarSkeleton } from "@mui/x-date-pickers/DayCalendarSkeleton";
import { useEffect, useRef, useState } from "react";
import { GetMonthActivity } from "../../../../../api/Summary";
import { useAppDispatch } from "../../../../../store/hooks";
import { useSelector } from "react-redux";
import { selectLogin } from "../../../../../store/user/selector";
import { selectCalendayDays } from "../../../../../store/root/selector";
import { actionSetCalendarDays } from "../../../../../store/root/reducer";

const initialValue = dayjs();

function ServerDay(props: PickersDayProps<Dayjs> & { highlightedDays?: number[] }) {
    const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;

    const isSelected = !props.outsideCurrentMonth && highlightedDays.indexOf(props.day.date()) >= 0;

    return (
        <Badge
            color="success"
            key={props.day.toString()}
            overlap="circular"
            badgeContent={isSelected ? "✔︎" : undefined}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
            }}
        >
            <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
        </Badge>
    );
}

export function Calendar() {
    const dispatch = useAppDispatch();

    const login = useSelector(selectLogin);
    const requestAbortController = useRef<AbortController | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const bank = useSelector(selectCalendayDays);

    const fetchHighlightedDays = (date: Dayjs) => {
        GetMonthActivity({
            year: date.year(),
            month: date.month() + 1,
            afterSuccess: (days: number[]) => {
                dispatch(actionSetCalendarDays(days));
                setIsLoading(false);
            },
            dispatch,
        });
    };

    useEffect(() => {
        if (!login) {
            return;
        }
        if (!bank.is_backend_data) {
            fetchHighlightedDays(initialValue);
        }
        // abort request on unmount
        return () => requestAbortController.current?.abort();
    }, [login, bank.is_backend_data]);

    const handleMonthChange = (date: Dayjs) => {
        if (requestAbortController.current) {
            // make sure that you are aborting useless requests
            // because it is possible to switch between months pretty quickly
            requestAbortController.current.abort();
        }

        setIsLoading(true);
        dispatch(actionSetCalendarDays([]));
        fetchHighlightedDays(date);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
                defaultValue={initialValue}
                loading={isLoading}
                onMonthChange={handleMonthChange}
                renderLoading={() => <DayCalendarSkeleton />}
                slots={{
                    day: ServerDay,
                }}
                slotProps={{
                    day: {
                        highlightedDays: bank.days,
                    } as any,
                }}
                readOnly
                sx={{ height: "300px" }}
            />
        </LocalizationProvider>
    );
}

export default Calendar;
